import React, { useState, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useToast } from "@chakra-ui/react";
import Layout from "./layout/Layout";
import Dashboard from "../pages/Dashboard";
import Bookings from "../pages/Bookings";
import AvailabilityNew from "../pages/AvailabilityNew";
import Embed from "../pages/Embed";
import Onboarding from "../pages/Onboarding";
import OnboardingConnectJobber from "../pages/OnboardingConnectJobber";
import OnboardingAvailability from "../pages/OnboardingAvailability";
import OnboardingTurnOn from "../pages/OnboardingTurnOn";
import OnboardingWaitlist from "../pages/OnboardingWaitlist";
import OnboardingWaitlistCustomize from "../pages/OnboardingWaitlistCustomize";
import Waitlist from "../pages/Waitlist";
import WaitlistOpening from "../pages/WaitlistOpening";
import DataStatus from "../pages/DataStatus";
import Leads from "../pages/Leads";
import LeadDetail from "../pages/LeadDetail";
import LeadImports from "../pages/LeadImports";
import GooglePlaces from "../pages/GooglePlaces";
import Billing from "../pages/Billing";

export default function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const toast = useToast();
  const lastErrorToastRef = useRef({ key: "", at: 0 });

  useEffect(() => {
    fetchCurrentUser();
  }, []);

  useEffect(() => {
    if (window.__samedayFetchWrapped) return;

    const originalFetch = window.fetch.bind(window);
    window.__samedayOriginalFetch = originalFetch;
    window.__samedayFetchWrapped = true;

    window.fetch = async (...args) => {
      try {
        const response = await originalFetch(...args);

        if (response.status >= 500) {
          let detailMessage = `HTTP ${response.status}`;
          try {
            const clone = response.clone();
            const contentType = clone.headers.get("content-type") || "";
            if (contentType.includes("application/json")) {
              const payload = await clone.json();
              detailMessage =
                payload?.error ||
                payload?.message ||
                (Array.isArray(payload?.errors) ? payload.errors.join(", ") : null) ||
                detailMessage;
            } else {
              const text = (await clone.text()).trim();
              if (text) detailMessage = text.slice(0, 240);
            }
          } catch (error) {
            // Keep default fallback message.
          }

          const url = typeof args[0] === "string" ? args[0] : args[0]?.url || "Unknown endpoint";
          window.dispatchEvent(
            new CustomEvent("global-api-error", {
              detail: {
                key: `${response.status}:${url}:${detailMessage}`,
                status: response.status,
                url,
                message: detailMessage,
              },
            })
          );
        }

        return response;
      } catch (error) {
        if (error?.name === "AbortError") {
          throw error;
        }

        const message = error?.message || "Network request failed";
        window.dispatchEvent(
          new CustomEvent("global-api-error", {
            detail: {
              key: `network:${message}`,
              status: "network",
              url: typeof args[0] === "string" ? args[0] : args[0]?.url || "Unknown endpoint",
              message,
            },
          })
        );
        throw error;
      }
    };
  }, []);

  useEffect(() => {
    const handleGlobalApiError = (event) => {
      const detail = event?.detail || {};
      const now = Date.now();
      const key = detail.key || `${detail.status || "error"}:${detail.url || "unknown"}`;

      if (lastErrorToastRef.current.key === key && now - lastErrorToastRef.current.at < 3000) {
        return;
      }

      lastErrorToastRef.current = { key, at: now };
      toast({
        title: detail.status === "network" ? "Network error" : "Server error",
        description: `${detail.url || "Unknown endpoint"}: ${detail.message || "Request failed"}`,
        status: "error",
        duration: 7000,
        isClosable: true,
        position: "top-right",
      });
    };

    window.addEventListener("global-api-error", handleGlobalApiError);
    return () => window.removeEventListener("global-api-error", handleGlobalApiError);
  }, [toast]);

  const fetchCurrentUser = async () => {
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      const response = await fetch("/api/current_user", {
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
      });

      const data = await response.json();
      setUser(data.user);
    } catch (err) {
      console.error("Failed to fetch current user:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const scheduleElement = <Bookings user={user} />;

  return (
    <BrowserRouter>
      <Routes>
        <Route element={<Layout user={user} />}>
          <Route path="/" element={user?.admin ? <Leads /> : <Dashboard user={user} />} />
          <Route path="/schedule" element={scheduleElement} />
          <Route path="/schedule/previous" element={scheduleElement} />
          <Route path="/schedule/previous/:requestId" element={scheduleElement} />
          <Route path="/waitlist" element={<Waitlist />} />
          <Route path="/waitlist/openings/:openingId" element={<WaitlistOpening />} />
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/onboarding/connect-provider" element={<OnboardingConnectJobber />} />
          <Route path="/onboarding/connect-jobber" element={<Navigate to="/onboarding/connect-provider" replace />} />
          <Route path="/onboarding/waitlist" element={<OnboardingWaitlist />} />
          <Route path="/onboarding/waitlist/customize" element={<OnboardingWaitlistCustomize />} />
          <Route path="/onboarding/availability" element={<OnboardingAvailability />} />
          <Route path="/onboarding/turn-on" element={<OnboardingTurnOn />} />
          <Route path="/availability" element={<AvailabilityNew />} />
          <Route path="/embed" element={<Embed />} />
          <Route path="/data-status" element={<DataStatus />} />
          <Route path="/billing" element={<Billing />} />
          <Route path="/leads" element={user?.admin ? <Leads /> : <Navigate to="/" replace />} />
          <Route path="/leads/:leadId" element={user?.admin ? <LeadDetail /> : <Navigate to="/" replace />} />
          <Route path="/lead-imports" element={user?.admin ? <LeadImports /> : <Navigate to="/" replace />} />
          <Route path="/google-places" element={user?.admin ? <GooglePlaces /> : <Navigate to="/" replace />} />
          {/* Catch-all route - redirects unknown paths to dashboard */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
